import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControlLabel, MenuItem, Paper, Select, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ReceiptLong as ReceiptIcon,
  CurrencyRupee as RupeeIcon,
  LocalOffer as TaxIcon,
  AccountBalanceWallet as NetIcon,
  Inventory as PlanIcon,
  Undo as RefundIcon,
  Discount as DiscountIcon,
  TrendingUp as TrendIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  PersonAdd as NewUserIcon,
  Replay as ReturningIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  useGetMarketplaceStatsQuery,
  type CreditNote,
} from '../../services/marketplaceApi';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toINR = (paise: number) => paise / 100;
export const fmtINR = (paise: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(toINR(paise));

export type Period =
  | 'today'
  | 'yesterday'
  | 'this_month'
  | 'last_month'
  | 'last_7'
  | 'last_30'
  | 'this_fy'
  | 'last_year'
  | 'all';

interface DateRange { after: string; before: string }

function getIST(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
}

function isoDate(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function getPeriodRange(period: Period): DateRange | null {
  if (period === 'all') return null;
  const now = getIST();
  const y = now.getFullYear(), mo = now.getMonth(), d = now.getDate();

  switch (period) {
    case 'today':
      return { after: isoDate(now), before: isoDate(now) };
    case 'yesterday': {
      const yest = new Date(y, mo, d - 1);
      return { after: isoDate(yest), before: isoDate(yest) };
    }
    case 'this_month':
      return { after: isoDate(new Date(y, mo, 1)), before: isoDate(now) };
    case 'last_month': {
      // Previous calendar month in IST
      const start = new Date(y, mo - 1, 1);
      const end = new Date(y, mo, 0); // last day of previous month
      return { after: isoDate(start), before: isoDate(end) };
    }
    case 'last_7':
      return { after: isoDate(new Date(y, mo, d - 6)), before: isoDate(now) };
    case 'last_30':
      return { after: isoDate(new Date(y, mo, d - 29)), before: isoDate(now) };
    case 'this_fy': {
      const fyStart = mo >= 3 ? new Date(y, 3, 1) : new Date(y - 1, 3, 1);
      return { after: isoDate(fyStart), before: isoDate(now) };
    }
    case 'last_year':
      return { after: isoDate(new Date(y - 1, mo, d)), before: isoDate(now) };
    default:
      return null;
  }
}

export const PERIOD_LABELS: Record<Period, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  this_month: 'This Month',
  last_month: 'Last Month',
  last_7: 'Last 7 Days', last_30: 'Last 30 Days',
  this_fy: 'This FY', last_year: 'Last 1 Year', all: 'All',
};

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: '#4caf50', PENDING: '#ff9800', FAILED: '#f44336',
  CANCELLED: '#9e9e9e', REFUNDED: '#7c4dff', PARTIAL_REFUND: '#e91e63',
};

const CHART_COLORS = ['#6366f1', '#22d3ee', '#f59e0b', '#10b981', '#f43f5e', '#a78bfa'];

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

interface KpiCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  sub?: string;
}

function DailyRevenueTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: {
    count?: number;
    gross?: number;
    new_count?: number;
    new_gross?: number;
    old_count?: number;
    old_gross?: number;
  } }>;
  label?: string;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const inr = (amount: number) => `₹${amount.toLocaleString('en-IN')}`;
  return (
    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.75 }}>
      <Typography variant="caption" fontWeight={700} display="block">{label}</Typography>
      <Typography variant="caption" display="block">New: {row.new_count ?? 0} · {inr(row.new_gross ?? 0)}</Typography>
      <Typography variant="caption" display="block">Repeat: {row.old_count ?? 0} · {inr(row.old_gross ?? 0)}</Typography>
      <Typography variant="caption" display="block">Total: {row.count ?? 0} · {inr(row.gross ?? 0)}</Typography>
    </Box>
  );
}

export function KpiCard({ label, value, icon, color, sub }: KpiCardProps) {
  return (
    <Card elevation={2} sx={{ height: '100%', borderLeft: `4px solid ${color}` }}>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}
              textTransform="uppercase" letterSpacing={0.8}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700} mt={0.5} color={color}>
              {value}
            </Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            bgcolor: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color,
          }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Dashboard
// ---------------------------------------------------------------------------

const MarketplaceDashboard: React.FC = () => {
  const [period, setPeriod]             = useState<Period>('this_month');
  const [statusFilter, setStatusFilter] = useState<string>('COMPLETED');
  const [excludeTest, setExcludeTest]   = useState(true);

  const range = useMemo(() => getPeriodRange(period), [period]);

  const statsParams = useMemo(() => ({
    ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
    ...(range ? { created_at_after: range.after, created_at_before: range.before } : {}),
    ...(excludeTest ? { is_test: false } : {}),
    ...(range ? { date_issued_after: range.after, date_issued_before: range.before } : {}),
  }), [statusFilter, range, excludeTest]);

  const { data: stats, isLoading, isError } = useGetMarketplaceStatsQuery(statsParams);

  const kpi = stats?.kpi ?? { total: 0, gross: 0, tax: 0, net: 0, disc: 0 };
  const planBreakdown = stats?.plan_breakdown ?? [];
  const refundKpi = stats?.refund_kpi ?? { count: 0, gross_refund: 0, tax_reversed: 0, net_refund: 0 };
  const statusDist = stats?.status_distribution ?? [];
  const byState = stats?.revenue_by_state ?? [];
  const buyer = stats?.buyer_split ?? { new_count: 0, new_gross: 0, old_count: 0, old_gross: 0 };
  const dailyTrend = useMemo(
    () => (stats?.daily_trend ?? []).map(d => ({
      ...d,
      gross: toINR(d.gross),
      new_gross: toINR(d.new_gross ?? 0),
      old_gross: toINR(d.old_gross ?? 0),
    })),
    [stats?.daily_trend],
  );
  const couponUsage = stats?.coupon_usage ?? [];
  const subsHealth = stats?.subscription_health ?? { total: 0, active: 0, inactive: 0 };
  const creditNotes = stats?.credit_notes ?? [];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <TrendIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h5" fontWeight={700}>Marketplace Dashboard</Typography>
            <Typography variant="caption" color="text.secondary">
              Orders · Revenue · Plans · Refunds
            </Typography>
          </Box>
        </Box>
        <Button component={RouterLink} to="/marketplace/churn" variant="outlined" size="small">
          Churned users
        </Button>
      </Box>

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup size="small" value={period} exclusive
          onChange={(_, v) => v && setPeriod(v)} sx={{ flexWrap: 'wrap' }}>
          {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
            <ToggleButton key={p} value={p} sx={{ fontSize: '0.72rem', px: 1.5 }}>
              {PERIOD_LABELS[p]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem />

        <Select size="small" value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="ALL">All Statuses</MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="FAILED">Failed</MenuItem>
          <MenuItem value="CANCELLED">Cancelled</MenuItem>
          <MenuItem value="REFUNDED">Refunded</MenuItem>
          <MenuItem value="PARTIAL_REFUND">Partial Refund</MenuItem>
        </Select>

        <FormControlLabel
          control={<Switch size="small" checked={excludeTest} onChange={e => setExcludeTest(e.target.checked)} />}
          label={<Typography variant="body2">Exclude Test Orders</Typography>}
        />

        {isLoading && <CircularProgress size={20} />}
      </Paper>

      {isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not fetch marketplace stats — the staff endpoint <code>/staff/marketplace/stats/</code> may not be registered yet.
        </Alert>
      )}

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label="Total Orders" value={kpi.total.toLocaleString('en-IN')}
            icon={<ReceiptIcon />} color="#6366f1"
            sub={`${PERIOD_LABELS[period]} · ${statusFilter === 'ALL' ? 'All statuses' : statusFilter}`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label="Gross Revenue" value={fmtINR(kpi.gross)}
            icon={<RupeeIcon />} color="#10b981" sub="Incl. GST" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label="GST Collected (18%)" value={fmtINR(kpi.tax)}
            icon={<TaxIcon />} color="#f59e0b" sub="CGST + SGST" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label="Net Revenue (ex-GST)" value={fmtINR(kpi.net)}
            icon={<NetIcon />} color="#22d3ee"
            sub={kpi.disc > 0 ? `Discounts: ${fmtINR(kpi.disc)}` : undefined} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label="New subscriptions" value={buyer.new_count.toLocaleString('en-IN')}
            icon={<NewUserIcon />} color="#10b981"
            sub={`${fmtINR(buyer.new_gross)} · first paid order`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard label="Returning subscriptions" value={buyer.old_count.toLocaleString('en-IN')}
            icon={<ReturningIcon />} color="#6366f1"
            sub={`${fmtINR(buyer.old_gross)} · bought before`} />
        </Grid>
      </Grid>

      {/* ── Charts ─────────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Daily trend */}
        {dailyTrend.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Daily revenue and purchase counts
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                Bars are revenue. Lines are new, repeat, and total purchase counts.
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={dailyTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="revenue" tick={{ fontSize: 10 }} tickFormatter={v => `₹${(Number(v) / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="orders" orientation="right" allowDecimals={false} tick={{ fontSize: 10 }} />
                  <RechartTooltip content={<DailyRevenueTooltip />} />
                  <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="revenue" dataKey="new_gross" name="New revenue" stackId="rev" fill="#10b981" />
                  <Bar yAxisId="revenue" dataKey="old_gross" name="Repeat revenue" stackId="rev" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="orders" type="monotone" dataKey="new_count" name="New purchases" stroke="#047857" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="orders" type="monotone" dataKey="old_count" name="Repeat purchases" stroke="#4338ca" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="orders" type="monotone" dataKey="count" name="Total purchases" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {/* Status pie */}
        {statusDist.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Orders by Status</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusDist as unknown as { count: number; status: string }[]} dataKey="count" nameKey="status"
                    cx="50%" cy="50%" outerRadius={70}
                    label={({ name, value }: any) => `${name}: ${value}`}
                    labelLine={false} fontSize={10}>
                    {statusDist.map((entry, i) => (
                      <Cell key={entry.status}
                        fill={STATUS_COLORS[entry.status] ?? CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                  <RechartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {/* Revenue by state */}
        {byState.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
                Gross Revenue by State — Top 10 (₹)
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={byState} layout="vertical" margin={{ top: 0, right: 8, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 10 }}
                    tickFormatter={v => `₹${(Number(v) / 100000).toFixed(1)}L`} />
                  <YAxis type="category" dataKey="state" tick={{ fontSize: 10 }} width={60} />
                  <RechartTooltip formatter={(v: number) => [fmtINR(v), 'Gross']} />
                  <Bar dataKey="gross" fill="#22d3ee" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {/* Subscription health */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>All-Time Subscription Health</Typography>
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="space-around">
              {[
                { label: 'Total Subscriptions', value: subsHealth.total, color: 'primary.main' },
                { label: 'Active',               value: subsHealth.active,   color: 'success.main' },
                { label: 'Expired / Cancelled',  value: subsHealth.inactive, color: 'text.secondary' },
              ].map(item => (
                <Box key={item.label} textAlign="center" flex={1}>
                  <Typography variant="h4" fontWeight={700} color={item.color}>
                    {item.value.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                </Box>
              ))}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Active rate:{' '}
              {subsHealth.total > 0
                ? `${((subsHealth.active / subsHealth.total) * 100).toFixed(1)}%`
                : '—'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Plan Breakdown ─────────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PlanIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>Plan Breakdown</Typography>
          <Typography variant="caption" color="text.secondary">
            (orders in selected period · subscriptions all-time)
          </Typography>
        </Box>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Orders</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Gross Revenue</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>GST</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Net Revenue</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Active Subs</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Inactive Subs</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {planBreakdown.map(row => {
                  // GST is embedded: gross = net + 18% GST → tax = gross * 18/118
                  const taxFromGross = Math.round(row.gross * 18 / 118);
                  const netFromGross = row.gross - taxFromGross;
                  return (
                    <TableRow key={row.plan_id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {row.plan_id}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={row.order_count} size="small"
                          color={row.order_count > 0 ? 'primary' : 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{fmtINR(row.gross)}</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>{fmtINR(taxFromGross)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>{fmtINR(netFromGross)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<ActiveIcon sx={{ fontSize: '14px !important' }} />}
                          label={row.active_subs} size="small"
                          color={row.active_subs > 0 ? 'success' : 'default'}
                          variant={row.active_subs > 0 ? 'filled' : 'outlined'} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<InactiveIcon sx={{ fontSize: '14px !important' }} />}
                          label={row.inactive_subs} size="small" color="default" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {planBreakdown.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No data for selected period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Refunds ────────────────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <RefundIcon color="error" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>Refunds &amp; Credit Notes</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Refunds Issued',  value: refundKpi.count.toString(),           color: '#7c4dff' },
            { label: 'Gross Refunded',  value: fmtINR(refundKpi.gross_refund),       color: '#f44336' },
            { label: 'Tax Reversed',    value: fmtINR(refundKpi.tax_reversed),       color: '#ff9800' },
            { label: 'Net Refunded',    value: fmtINR(refundKpi.net_refund),         color: '#e91e63' },
          ].map(k => (
            <Grid key={k.label} size={{ xs: 6, md: 3 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: k.color + '15', borderLeft: `3px solid ${k.color}` }}>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h6" fontWeight={700} color={k.color}>{k.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}><CircularProgress /></Box>
        ) : creditNotes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            No refunds in the selected period
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>CN No.</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>FY</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Gross Refund</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Tax Reversed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Net Refund</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Razorpay ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditNotes.map((cn: CreditNote) => (
                  <TableRow key={cn.id} hover>
                    <TableCell>{new Date(cn.date_issued).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell><code>{cn.credit_note_no}</code></TableCell>
                    <TableCell>{cn.financial_year}</TableCell>
                    <TableCell>
                      <Tooltip title={cn.order}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {cn.order?.slice(0, 8)}…
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{fmtINR(cn.gross_refund)}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main' }}>{fmtINR(cn.tax_reversed)}</TableCell>
                    <TableCell align="right">{fmtINR(cn.refund_amount)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {cn.razorpay_refund_id ?? '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Coupon Usage ───────────────────────────────────────────────────── */}
      {couponUsage.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <DiscountIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>Coupon Usage</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Coupon Code</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Times Used</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Total Discount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {couponUsage.map(c => (
                  <TableRow key={c.code} hover>
                    <TableCell>
                      <Chip label={c.code} size="small" variant="outlined" color="primary" />
                    </TableCell>
                    <TableCell align="right">{c.count}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{fmtINR(c.total_discount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" display="block" textAlign="right" mt={1}>
        All amounts in INR. GST @ 18%. Data reflects {excludeTest ? 'live' : 'live + test'} orders.
      </Typography>
    </Box>
  );
};

export default MarketplaceDashboard;
